import type { App, Directive } from 'vue'
import { demoState as state } from './state'
import type { DemoStep } from './types'
import type { ElementId } from './elements-registry'

const DEBUG = import.meta.env.VITE_APP_DEV_SHOW_DEMO_UIDS

interface Config {
  uid: ElementId
  highlightOff?: boolean
}

function isEqual(confA?: Config, confB?: Config) {
  if (confA?.highlightOff !== confB?.highlightOff) return false
  if (confA?.uid !== confB?.uid) return false
  return true
}

const highlightEl = document.createElement('div')
highlightEl.className =
  'interactive-vibrant-tertiary rounded absolute animate-small-ping pointer-events-none z-50'

const objToConfMap: Map<HTMLElement, Config> = new Map()
const uidToObjMap: Map<ElementId, HTMLElement> = new Map()

function isActive(uid: ElementId, step: DemoStep) {
  if (state.status === 'complete') return false

  const bool = (() => {
    for (const aeId of step.activeElements ?? []) {
      if (aeId.includes('*')) {
        if (uid.includes(aeId.replace('/*', ''))) return true
      }
      if (aeId === uid) return true
    }
  })()

  if (state.stepStatus === 'complete') {
    return bool && Boolean(step.canContinueInteracting)
  }
  return bool
}
function isHighlighted(uid: ElementId, step: DemoStep) {
  if (state.status === 'complete') return false
  if (state.stepStatus === 'complete') return false
  return step.highlightElements?.includes(uid)
}

function updateElement(el: HTMLElement, config: Config) {
  if (!state.active) return
  const step = state.steps[state.currentStepIdx] as DemoStep

  resetElement(el)

  // manage hidden state
  el.dataset.demoHidden = `${
    step &&
    (state.flow?.hiddenElements?.includes(config.uid) ||
      step.hiddenElements?.includes(config.uid))
  }`

  // manage disabled state
  el.dataset.demoDisabled = `${!step || !isActive(config.uid, step)}`
}

function resetElement(el: HTMLElement) {
  el.dataset.demoDisabled = ''
  el.dataset.demoHidden = ''
}

function _maybeHighlight() {
  highlightEl.remove()

  if (!state.active) return

  const step = state.steps[state.currentStepIdx] as DemoStep
  if (!step) return

  let toHighlight: HTMLElement | undefined
  for (const uid of step.highlightElements ?? []) {
    if (!isHighlighted(uid, step)) continue
    if (!uidToObjMap.has(uid)) continue

    const el = uidToObjMap.get(uid)!
    if (objToConfMap.get(el)?.highlightOff) continue

    toHighlight = el
  }

  if (toHighlight) {
    const el = toHighlight
    const rect = el.getBoundingClientRect()
    highlightEl.setAttribute(
      'style',
      `width: ${rect.width}px; height: ${rect.height}px; top: ${rect.top}px; left: ${rect.left}px`,
    )
    document.body.append(highlightEl)
  }
}

function maybeHighlight() {
  setTimeout(_maybeHighlight, 500)
}

export function update() {
  if (!state.active) return
  for (const [el, conf] of objToConfMap.entries()) {
    updateElement(el, conf)
  }
  maybeHighlight()
}

let clickAttemptCount = 0
export function clickElement(uid: ElementId) {
  setTimeout(() => {
    const el = uidToObjMap.get(uid)
    if (!el) {
      if (clickAttemptCount >= 4) {
        clickAttemptCount = 0
      } else {
        clickAttemptCount++
        clickElement(uid)
      }
      return
    }
    el.click()
    clickAttemptCount = 0
  }, 200)
}

export function isTargetElement(element: HTMLElement, uid: ElementId) {
  const el = uidToObjMap.get(uid)
  return el === element
}

export function reset() {
  highlightEl.remove()
  for (const el of objToConfMap.keys()) {
    resetElement(el)
  }
  objToConfMap.clear()
  uidToObjMap.clear()
}

export type DemoDirective = Directive<HTMLElement, Config | undefined | null>

const directive: DemoDirective = {
  mounted(el, binding) {
    const config = binding.value
    if (!config) return
    setTimeout(() => {
      objToConfMap.set(el, config)
      uidToObjMap.set(config.uid, el)
      updateElement(el, config)
      maybeHighlight()
    }, 10)

    // util for devs to identify the element demo-id on hover
    if (DEBUG) {
      el.style.pointerEvents = 'all'
      el.addEventListener('mouseenter', () => {
        // eslint-disable-next-line no-console
        console.log(`demo-el uid: ${config.uid}`)
      })
    }
  },
  // mounted(el, binding) {
  // },
  updated(el, binding) {
    const config = binding.value
    if (!config) return
    if (isEqual(config, objToConfMap.get(el))) return
    setTimeout(() => {
      objToConfMap.set(el, config)
      uidToObjMap.set(config.uid, el)
      updateElement(el, config)
      maybeHighlight()
    }, 10)
  },
  unmounted(el) {
    const config = objToConfMap.get(el)
    if (!config) return
    setTimeout(() => {
      objToConfMap.delete(el)
      uidToObjMap.delete(config.uid)
      resetElement(el)
      maybeHighlight()
    }, 10)
  },
}

export default {
  install(app: App) {
    app.directive('demo-el', directive)
  },
}
